<script lang="ts">
	import { Badge } from '$lib/components/ui/badge';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import { localizeHref } from '@/paraglide/runtime';
	import { Sailboat } from '@lucide/svelte';
	import Anchor from '@lucide/svelte/icons/anchor';
	import ArrowRight from '@lucide/svelte/icons/arrow-right';
	import Award from '@lucide/svelte/icons/award';
	import CircleCheck from '@lucide/svelte/icons/check-circle';
	import DollarSign from '@lucide/svelte/icons/dollar-sign';
	import Handshake from '@lucide/svelte/icons/handshake';
	import Heart from '@lucide/svelte/icons/heart';
	import Mail from '@lucide/svelte/icons/mail';
	import MessageCircle from '@lucide/svelte/icons/message-circle';
	import Navigation from '@lucide/svelte/icons/navigation';
	import Phone from '@lucide/svelte/icons/phone';
	import Shield from '@lucide/svelte/icons/shield';
	import Ship from '@lucide/svelte/icons/ship';
	import TrendingDown from '@lucide/svelte/icons/trending-down';
	import Users from '@lucide/svelte/icons/users';
	import Wallet from '@lucide/svelte/icons/wallet';
	import Wrench from '@lucide/svelte/icons/wrench';

	// Beneficios específicos para taberneros
	const benefits = [
		{
			icon: Wallet,
			title: 'Adiós a los 3.000-8.000€ anuales',
			description:
				'Convierte ese pozo sin fondo del mantenimiento en ingresos. Tu barco paga su propio amarre, antifouling y más.',
			highlight: true
		},
		{
			icon: Users,
			title: 'Navega más, gasta menos',
			description:
				'Comparte gastos de salida con otros taberneros. Gasoil, puertos... todo dividido, como debe ser.',
			highlight: false
		},
		{
			icon: Shield,
			title: 'Seguro y legal, sin historias',
			description:
				'Todo en regla con la nueva normativa 2025. Nosotros nos encargamos del papeleo.',
			highlight: false
		},
		{
			icon: Handshake,
			title: 'Tu barco, tus reglas',
			description:
				'Tú decides cuándo, con quién y cómo. Mantén el control total de tu embarcación.',
			highlight: true
		}
	];

	// Testimonios reales (simulados) de taberneros
	const testimonials = [
		{
			name: 'Carlos "Tramontana"',
			boat: 'Bavaria 38',
			port: 'Valencia',
			message:
				'¡Por fin! Llevaba años con el barco parado 10 meses al año. Ahora sale cada semana y me paga el amarre solo.',
			savings: '5.200€/año'
		},
		{
			name: 'Javi del Canal 77',
			boat: 'Dufour 375',
			port: 'Barcelona',
			message:
				'Entre el antifouling, motor, velas... se me iban 6k al año. Ahora eso lo pagan las salidas compartidas.',
			savings: '6.000€/año'
		},
		{
			name: 'Miguel "Levante"',
			boat: 'Jeanneau SO 349',
			port: 'Palma',
			message:
				'Lo mejor es conocer gente con la misma pasión. Ya tengo mi tripulación fija para regatas.',
			savings: '4.500€/año'
		}
	];

	// Problemas que resolvemos
	const painPoints = [
		'El dichoso antifouling cada año',
		'Revisión del motor que no perdona',
		'El amarre que sube y sube',
		'Las velas que se van degradando',
		'El seguro que apenas usas',
		'La electrónica que se estropea'
	];

	// Estadísticas objetivo
	const stats = [
		{ value: '100+', label: 'Días navegando/año' },
		{ value: '85%', label: 'Menos gastos' },
		{ value: '3-7', label: 'Salidas/mes posibles' },
		{ value: '100%', label: 'Legal y seguro' }
	];
</script>

<svelte:head>
	<title>¡Bienvenido Tabernero! - Tu barco puede pagarse solo | Zarpar</title>
	<meta
		name="description"
		content="Únete a cientos de armadores de La Taberna del Puerto que ya navegan más y gastan menos. Convierte los gastos de mantenimiento en ingresos."
	/>
	<meta property="og:title" content="Taberneros: Vuestros barcos pueden pagarse solos" />
	<meta
		property="og:description"
		content="La comunidad de La Taberna del Puerto ya lo está haciendo. Navega más, gasta menos, conoce gente increíble."
	/>
	<meta property="og:image" content="/logo-laTaberna.png" />
</svelte:head>

<!-- Hero Section - Máximo impacto above the fold -->
<section
	class="relative flex min-h-[calc(100vh-64px)] items-center bg-linear-to-b from-blue-900 via-blue-800 to-blue-700"
>
	<!-- Patrón de olas sutiles -->
	<div class="absolute inset-0 opacity-10">
		<svg class="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
			<path d="M0,20 Q25,10 50,20 T100,20 L100,100 L0,100 Z" fill="white"></path>
			<path d="M0,40 Q25,30 50,40 T100,40 L100,100 L0,100 Z" fill="white"></path>
			<path d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z" fill="white"></path>
		</svg>
	</div>

	<div class="relative z-10 container mx-auto mt-2 mb-4 px-4">
		<div class="mx-auto max-w-6xl">
			<!-- Logo y saludo -->
			<div class="mb-8 flex flex-row items-center justify-center gap-4">
				<img src="/logo-laTaberna.png" alt="La Taberna del Puerto" class="h-16 sm:h-20" />
				<div class="text-center text-white sm:text-left">
					<span class="block text-sm opacity-90 sm:text-base">Canal 77 VHF</span>
					<span class="text-lg font-semibold sm:text-xl"
						>¡Gracias por pasarte, te invito a una ronda! 🍺</span
					>
				</div>
			</div>

			<!-- Titular principal -->
			<h1
				class="mb-6 text-center text-3xl leading-tight font-bold text-white sm:text-4xl md:text-5xl lg:text-6xl"
			>
				Cofrade, tu barco te está<br />
				<span class="text-yellow-400">costando una pasta</span><br />
				cuando podría <span class="text-green-400">pagarse solo</span>
			</h1>

			<!-- Subtítulo -->
			<p
				class="mx-auto mb-6 max-w-4xl px-4 text-center text-lg text-blue-100 sm:text-xl md:text-2xl"
			>
				Mientras lees esto, dudas si hacer esa reparacion o tirar mientras aguante y ahorrarte los
				euros.
				<strong class="text-white">Es hora de darle la vuelta a la tortilla.</strong>
			</p>

			<!-- Misión de Zarpar -->
			<div
				class="mx-auto mb-8 max-w-3xl rounded-xl border border-white/20 bg-white/5 p-4 backdrop-blur-sm sm:p-6"
			>
				<p class="text-base text-white sm:text-lg">
					<span class="font-semibold text-yellow-400">Nuestra misión:</span> Que los barcos naveguen
					<strong class="text-green-400">100+ días al año</strong> en vez de 15-20. Que desaparezacn
					los barcos semi-abandonados y sin uso por falta de mantenimiento de los puertos. Ayudar a
					los armadores a que el mantenimiento sea mas asequible.
					<strong class="mt-2 block text-blue-200"
						>Más días en el mar, menos días en el amarre.</strong
					>
				</p>
			</div>
			<!-- CTA Principal -->
			<div class="mb-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
				<Button
					href={localizeHref('/anadir-barco')}
					size="lg"
					class="w-full transform bg-green-500 px-6 py-4 text-base text-white shadow-xl transition-all hover:scale-105 hover:bg-green-600 hover:shadow-2xl sm:w-auto sm:px-8 sm:py-6 sm:text-lg"
				>
					<Anchor class="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
					Publicar mi barco GRATIS
				</Button>
				<Button
					variant="outline"
					size="lg"
					class="w-full border-white px-6 py-4 text-base  hover:bg-white hover:text-blue-900 sm:w-auto sm:px-8 sm:py-6 sm:text-lg"
					onclick={() =>
						document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' })}
				>
					Ver cómo funciona
					<ArrowRight class="ml-2 h-5 w-5" />
				</Button>
			</div>
			<!-- Propuesta de valor rápida -->
			<div class="mx-auto mb-8 grid max-w-3xl grid-cols-1 gap-4 px-4 sm:grid-cols-3">
				<div class="rounded-lg bg-white/10 p-4 text-center backdrop-blur-sm">
					<TrendingDown class="mx-auto mb-2 h-8 w-8 text-green-400" />
					<div class="font-semibold text-white">-70% gastos</div>
					<div class="text-sm text-blue-200">mantenimiento</div>
				</div>
				<div class="rounded-lg bg-white/10 p-4 text-center backdrop-blur-sm">
					<Sailboat class="mx-auto mb-2 h-8 w-8 text-yellow-400" />
					<div class="font-semibold text-white">+300% uso</div>
					<div class="text-sm text-blue-200">de tu barco</div>
				</div>
				<div class="rounded-lg bg-white/10 p-4 text-center backdrop-blur-sm">
					<Users class="mx-auto mb-2 h-8 w-8 text-blue-400" />
					<div class="font-semibold text-white">Más navegación</div>
					<div class="text-sm text-blue-200">para todos</div>
				</div>
			</div>

			<!-- Badge de urgencia -->
			<div class="text-center">
				<Badge class="animate-pulse bg-yellow-500 px-4 py-2 text-sm text-gray-900 sm:text-base">
					⚡ Nueva ley 2025: Ahora SÍ puedes alquilar tu Lista 7ª legalmente
				</Badge>
			</div>
		</div>
	</div>
</section>

<!-- Sección de dolor - Los gastos que conocen bien -->
<section class="bg-gray-50 py-12 sm:py-16">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-4xl text-center">
			<h2 class="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
				Sabemos perfectamente lo que te gastas al año
			</h2>
			<p class="mb-8 text-lg text-gray-600 sm:text-xl">
				Y sabemos que cada vez que pasas por el barco sin usarlo, se te cae el alma a los pies
			</p>

			<div class="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
				{#each painPoints as pain (pain)}
					<div class="rounded-lg border border-red-200 bg-red-50 p-3 sm:p-4">
						<Wrench class="mx-auto mb-2 h-5 w-5 text-red-500 sm:h-6 sm:w-6" />
						<p class="text-sm text-gray-700 sm:text-base">{pain}</p>
					</div>
				{/each}
			</div>

			<div
				class="rounded-xl bg-linear-to-r from-red-500 to-red-600 p-4 text-white shadow-xl sm:p-8"
			>
				<p class="mb-2 text-2xl font-bold sm:text-3xl md:text-4xl">Total: 3.000€ - 8.000€ al año</p>
				<p class="text-base opacity-90 sm:text-lg">
					Por un barco que usas 15-20 días si hay suerte
				</p>
			</div>
		</div>
	</div>
</section>

<!-- La solución -->
<section id="como-funciona" class="bg-white py-12 sm:py-16">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-5xl">
			<div class="mb-12 text-center">
				<Badge class="mb-4 bg-green-100 text-green-800">LA SOLUCIÓN</Badge>
				<h2 class="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
					Dos formas de que tu barco se pague solo
				</h2>
				<p class="text-lg text-gray-600 sm:text-xl">Elige la que mejor te vaya (o combina ambas)</p>
			</div>

			<div class="grid gap-6 sm:gap-8 md:grid-cols-2">
				<!-- Opción 1: Alquiler -->
				<Card.Root class="border-2 transition-all hover:border-blue-500 hover:shadow-xl">
					<Card.Header class="bg-blue-50">
						<div class="flex items-center gap-4">
							<DollarSign class="h-10 w-10 text-blue-600 sm:h-12 sm:w-12" />
							<div>
								<h3 class="text-xl font-bold text-gray-900 sm:text-2xl">
									Alquílalo cuando no navegues
								</h3>
								<p class="text-sm text-gray-600 sm:text-base">
									Lista 7ª - 100% legal con nueva normativa
								</p>
							</div>
						</div>
					</Card.Header>
					<Card.Content class="pt-6">
						<ul class="space-y-3">
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">300-600€ por día de alquiler</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Nosotros gestionamos todo</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Seguro incluido en cada alquiler</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Tú eliges las fechas disponibles</span>
							</li>
						</ul>
						<div class="mt-6 rounded-lg bg-green-50 p-4">
							<p class="text-sm font-semibold text-green-800 sm:text-base">
								Media: 15 días alquilados = 6.000€ año
							</p>
						</div>
					</Card.Content>
				</Card.Root>

				<!-- Opción 2: Compartir -->
				<Card.Root class="border-2 transition-all hover:border-yellow-500 hover:shadow-xl">
					<Card.Header class="bg-yellow-50">
						<div class="flex items-center gap-4">
							<Users class="h-10 w-10 text-yellow-600 sm:h-12 sm:w-12" />
							<div>
								<h3 class="text-xl font-bold text-gray-900 sm:text-2xl">
									Comparte gastos de navegación
								</h3>
								<p class="text-sm text-gray-600 sm:text-base">Como BlaBlaCar pero en el mar</p>
							</div>
						</div>
					</Card.Header>
					<Card.Content class="pt-6">
						<ul class="space-y-3">
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">50-100€ por persona y salida</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Conoces gente con tu misma pasión</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Navegas más acompañado</span>
							</li>
							<li class="flex items-start gap-2">
								<CircleCheck class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
								<span class="text-sm sm:text-base">Creas tu tripulación habitual</span>
							</li>
						</ul>
						<div class="mt-6 rounded-lg bg-green-50 p-4">
							<p class="text-sm font-semibold text-green-800 sm:text-base">
								Media: 2 salidas/mes = 4.800€ año
							</p>
						</div>
					</Card.Content>
				</Card.Root>
			</div>

			<!-- CTA después de explicar -->
			<div class="mt-8 text-center">
				<Button
					href={localizeHref('/anadir-barco')}
					size="lg"
					class="transform bg-green-500 px-6 py-4 text-base text-white shadow-xl transition-all hover:scale-105 hover:bg-green-600 hover:shadow-2xl sm:px-8 sm:py-6 sm:text-lg"
				>
					<Ship class="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
					Empezar ahora (es gratis)
				</Button>
				<p class="mt-3 text-sm text-gray-500">
					Sin permanencia • Sin comisiones ocultas • Cancela cuando quieras
				</p>
			</div>
		</div>
	</div>
</section>

<!-- Testimonios de taberneros -->
<!--
<section class="bg-linear-to-b from-blue-50 to-white py-12 sm:py-16">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-6xl">
			<div class="mb-12 text-center">
				<Badge class="mb-4 bg-blue-100 text-blue-800">TABERNEROS COMO TÚ</Badge>
				<h2 class="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
					Así es como funciona para taberneros como tú
				</h2>
				<p class="text-lg text-gray-600 sm:text-xl">
					Historias reales de armadores que han transformado sus gastos en ingresos
				</p>
			</div>

			<div class="grid gap-6 md:grid-cols-3">
				{#each testimonials as testimonial}
					<Card.Root class="bg-white transition-shadow hover:shadow-xl">
						<Card.Content class="p-6">
							<div class="mb-4 flex items-start gap-4">
								<div class="shrink-0 rounded-full bg-blue-100 p-3">
									<Ship class="h-6 w-6 text-blue-600" />
								</div>
								<div>
									<h4 class="font-semibold text-gray-900">{testimonial.name}</h4>
									<p class="text-sm text-gray-500">{testimonial.boat} • {testimonial.port}</p>
								</div>
							</div>
							<p class="mb-4 italic text-gray-700">"{testimonial.message}"</p>
							<div class="rounded-lg bg-green-50 p-3 text-center">
								<p class="text-xs text-gray-600">Ahorro anual:</p>
								<p class="text-2xl font-bold text-green-600">{testimonial.savings}</p>
							</div>
						</Card.Content>
					</Card.Root>
				{/each}
			</div>

			<div class="mt-12 grid grid-cols-2 gap-4 rounded-xl bg-white p-6 shadow-lg sm:grid-cols-4">
				{#each stats as stat}
					<div class="text-center">
						<div class="text-2xl font-bold text-blue-600 sm:text-3xl">{stat.value}</div>
						<div class="text-sm text-gray-600">{stat.label}</div>
					</div>
				{/each}
			</div>
		</div>
	</div>
</section>
-->
<!-- Beneficios específicos -->
<section class="bg-white py-12 sm:py-16">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-5xl">
			<div class="mb-12 text-center">
				<h2 class="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
					¿Por qué los taberneros confían en nosotros?
				</h2>
			</div>

			<div class="grid gap-6 md:grid-cols-2">
				{#each benefits as benefit (benefit.title)}
					{@const Icon = benefit.icon}
					<Card.Root class={benefit.highlight ? 'border-2 border-blue-500 shadow-lg' : ''}>
						<Card.Content class="p-6">
							<div class="flex items-start gap-4">
								<div class="shrink-0 rounded-lg bg-blue-100 p-3">
									<Icon class="h-6 w-6 text-blue-600" />
								</div>
								<div>
									<h3 class="mb-2 text-lg font-semibold text-gray-900">{benefit.title}</h3>
									<p class="text-gray-600">{benefit.description}</p>
								</div>
							</div>
						</Card.Content>
					</Card.Root>
				{/each}
			</div>
		</div>
	</div>
</section>

<!-- FAQ rápido -->
<!--
<section class="bg-gray-50 py-12 sm:py-16">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-4xl">
			<h2 class="mb-8 text-center text-2xl font-bold text-gray-900 sm:text-3xl">
				Las dudas de siempre
			</h2>

			<div class="space-y-4">
				<Card.Root>
					<Card.Content class="p-6">
						<h3 class="mb-2 text-lg font-semibold">¿Y si me rompen algo?</h3>
						<p class="text-gray-600">
							Seguro integral en cada salida. Además, verificamos a cada usuario y tienes fianza de
							daños. Dormirás tranquilo.
						</p>
					</Card.Content>
				</Card.Root>

				<Card.Root>
					<Card.Content class="p-6">
						<h3 class="mb-2 text-lg font-semibold">¿Es legal con Lista 7ª?</h3>
						<p class="text-gray-600">
							100% legal con la nueva normativa 2025. Te ayudamos con todo el papeleo y
							declaraciones. Sin sorpresas con Hacienda.
						</p>
					</Card.Content>
				</Card.Root>

				<Card.Root>
					<Card.Content class="p-6">
						<h3 class="mb-2 text-lg font-semibold">¿Puedo usar mi barco cuando quiera?</h3>
						<p class="text-gray-600">
							Por supuesto. Tú marcas tu calendario. Bloquea las fechas que quieras y el resto las
							gestionamos nosotros.
						</p>
					</Card.Content>
				</Card.Root>

				<Card.Root>
					<Card.Content class="p-6">
						<h3 class="mb-2 text-lg font-semibold">¿Cuánto cobráis vosotros?</h3>
						<p class="text-gray-600">
							20% de comisión solo cuando generas ingresos. Si no ganas, nosotros tampoco. Así de
							simple y transparente.
						</p>
					</Card.Content>
				</Card.Root>
			</div>
		</div>
	</div>
</section>
-->
<!-- CTA Final con urgencia -->
<section class="bg-linear-to-r from-blue-900 to-blue-700 py-16 text-white sm:py-20">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-4xl text-center">
			<h2 class="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl">
				Cofrade, deja de quemar dinero
			</h2>
			<p class="mb-8 text-xl text-blue-100 sm:text-2xl">
				Cada mes que pasa sin tu barco generando ingresos<br class="hidden sm:block" />
				son <strong class="text-yellow-400">400€ que tiras por la borda</strong>
			</p>

			<div class="mx-auto mb-8 max-w-2xl rounded-xl bg-white/10 p-6 backdrop-blur-sm sm:p-8">
				<p class="mb-4 text-lg sm:text-xl">
					<strong>Oferta especial Taberneros:</strong>
				</p>
				<ul class="mb-6 space-y-2 text-left text-base sm:text-lg">
					<li class="flex items-center gap-2">
						<CircleCheck class="h-5 w-5 shrink-0 text-green-400" />
						<span>Alta gratis </span>
					</li>
					<li class="flex items-center gap-2">
						<CircleCheck class="h-5 w-5 shrink-0 text-green-400" />
						<span>Sesión de fotos profesionales gratis (puertos Rias Baixas)</span>
					</li>
					<li class="flex items-center gap-2">
						<CircleCheck class="h-5 w-5 shrink-0 text-green-400" />
						<span>Optimizacion de anuncio gratis</span>
					</li>
				</ul>
				<Badge class="animate-pulse bg-yellow-500 px-4 py-2 text-sm text-gray-900 sm:text-base">
					⏰ Oferta válida solo este mes
				</Badge>
			</div>

			<Button
				href={localizeHref('/anadir-barco')}
				size="lg"
				class="hover:shadow-3xl transform animate-bounce bg-green-500 px-8 py-6 text-lg text-white shadow-2xl transition-all hover:scale-105 hover:bg-green-600 sm:px-10 sm:py-8 sm:text-xl"
			>
				<Anchor class="mr-3 h-6 w-6 sm:h-8 sm:w-8" />
				Añadir mi barco AHORA
			</Button>

			<p class="mt-6 text-sm text-blue-200 sm:text-base">
				O llámanos al <a href="tel:+34679855762" class="font-semibold text-yellow-400 underline"
					>679 855 762</a
				> y te lo explicamos en 5 minutos
			</p>

			<!-- Trust badges -->
			<div class="mt-12 flex flex-wrap justify-center gap-6">
				<div class="flex items-center gap-2">
					<Shield class="h-6 w-6 text-green-400" />
					<span class="text-sm">100% Seguro</span>
				</div>
				<div class="flex items-center gap-2">
					<Award class="h-6 w-6 text-yellow-400" />
					<span class="text-sm">Plataforma verificada</span>
				</div>
				<div class="flex items-center gap-2">
					<Heart class="h-6 w-6 text-red-400" />
					<span class="text-sm">Taberneros felices</span>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- Footer específico -->
<footer class="bg-gray-900 py-8 text-white sm:py-12">
	<div class="container mx-auto px-4">
		<div class="mx-auto max-w-4xl text-center">
			<div class="mb-6 flex items-center justify-center gap-4">
				<img src="/logo-laTaberna.png" alt="La Taberna del Puerto" class="h-12" />
				<Navigation class="h-8 w-8 text-blue-400" />
				<img src="/favicon.svg" alt="Zarpar" class="h-12" />
			</div>

			<p class="mb-4 text-lg">De cofrade a cofrade: esto funciona de verdad</p>

			<div class="flex flex-wrap justify-center gap-6 text-sm">
				<a
					href="tel:+34679855762"
					class="flex items-center gap-2 transition-colors hover:text-yellow-400"
				>
					<Phone class="h-4 w-4" />
					679 855 762
				</a>
				<a
					href="mailto:info@zarpar.eu"
					class="flex items-center gap-2 transition-colors hover:text-yellow-400"
				>
					<Mail class="h-4 w-4" />
					info@zarpar.eu
				</a>
				<a
					href="https://wa.me/34679855762"
					class="flex items-center gap-2 transition-colors hover:text-green-400"
				>
					<MessageCircle class="h-4 w-4" />
					WhatsApp
				</a>
			</div>

			<p class="mt-6 text-xs text-gray-400">
				Zarpar.eu - La plataforma de los que aman el mar<br />
				Compatible con la nueva normativa de Lista 7ª 2025
			</p>
		</div>
	</div>
</footer>
